<template>
  <span class="dh-badge" :class="badgeClass">
    <!-- 主要内容 -->
    <slot></slot>

    <!-- 徽标内容 -->
    <sup
      v-if="showBadge"
      class="badge-content"
      :class="contentClass"
      :style="contentStyle"
    >
      <slot name="badge">
        {{ displayValue }}
      </slot>
    </sup>
  </span>
</template>

<script setup lang="ts">
/**
 * 徽标组件
 * 用于显示数字、状态或其他标记信息
 * @author 系统
 * @date 2024-01-01
 */

import { computed } from 'vue'

interface Props {
  /** 徽标显示的值 */
  value?: string | number
  /** 最大显示数字，超过显示 + */
  max?: number
  /** 是否显示小红点 */
  isDot?: boolean
  /** 是否隐藏徽标 */
  hidden?: boolean
  /** 徽标类型 */
  type?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** 徽标大小 */
  size?: 'small' | 'default' | 'large'
  /** 自定义颜色 */
  color?: string
  /** 偏移量 [x, y] */
  offset?: [number, number]
}

const props = withDefaults(defineProps<Props>(), {
  max: 99,
  isDot: false,
  hidden: false,
  type: 'danger',
  size: 'default'
})

// 计算属性
const showBadge = computed(() => {
  if (props.hidden) return false
  if (props.isDot) return true
  return props.value !== undefined && props.value !== null && props.value !== ''
})

const displayValue = computed(() => {
  if (props.isDot) return ''

  const value = props.value
  if (typeof value === 'number' && value > props.max) {
    return `${props.max}+`
  }

  return value
})

const badgeClass = computed(() => {
  return [
    'badge-wrapper',
    `badge-${props.size}`
  ]
})

const contentClass = computed(() => {
  return [
    'badge-sup',
    `badge-${props.type}`,
    `badge-size-${props.size}`,
    {
      'badge-dot': props.isDot,
      'badge-count': !props.isDot
    }
  ]
})

const contentStyle = computed(() => {
  const style: Record<string, any> = {}

  // 自定义颜色
  if (props.color) {
    style.backgroundColor = props.color
    style.borderColor = props.color
  }

  // 偏移量
  if (props.offset) {
    const [x, y] = props.offset
    style.transform = `translate(${x}px, ${y}px)`
  }

  return style
})
</script>

<style scoped>
.dh-badge {
  position: relative;
  display: inline-block;
  line-height: 1;
}

.badge-content {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  transform: translate(50%, -50%);
  transform-origin: 100% 0%;
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  color: #fff;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  background-color: var(--el-color-danger);
  border: 1px solid #fff;
  box-sizing: border-box;
  white-space: nowrap;
}

/* 类型样式 */
.badge-primary {
  background-color: var(--el-color-primary);
}

.badge-success {
  background-color: var(--el-color-success);
}

.badge-warning {
  background-color: var(--el-color-warning);
}

.badge-danger {
  background-color: var(--el-color-danger);
}

.badge-info {
  background-color: var(--el-color-info);
}

/* 大小样式 */
.badge-size-small .badge-content {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  line-height: 16px;
  border-radius: 8px;
}

.badge-size-large .badge-content {
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  font-size: 14px;
  line-height: 24px;
  border-radius: 12px;
}

/* 小红点样式 */
.badge-dot {
  width: 8px !important;
  height: 8px !important;
  min-width: 8px !important;
  padding: 0 !important;
  border-radius: 50% !important;
}

.badge-size-small .badge-dot {
  width: 6px !important;
  height: 6px !important;
  min-width: 6px !important;
}

.badge-size-large .badge-dot {
  width: 10px !important;
  height: 10px !important;
  min-width: 10px !important;
}

/* 动画效果 */
.badge-content {
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}

/* 数字为0时隐藏 */
.badge-count:empty {
  display: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .badge-content {
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
  }

  .badge-size-large .badge-content {
    font-size: 12px;
    min-width: 20px;
    height: 20px;
    line-height: 20px;
    padding: 0 6px;
  }
}
</style>
